/**
 * Company settings (Employer Phase 1, PRD §14): a grouped drill-in menu
 * ("Company page settings") with two groups:
 *   - Page:       Company profile, Official website          → edit-profile
 *   - Publishing: Page address (→ edit-profile), Search appearance
 *                 (in-sheet subview), Domain & embedding (P1, "Soon"),
 *                 Advanced SEO (P1, "Soon")
 *
 * The Search appearance subview re-homes the P0 controls one level deeper:
 * search title/description with counters, indexing toggle, social text with
 * use-search-text, page language, and the Tabbio-branding row. The subview
 * pattern (local view state + back row with an RTL-flipped chevron) mirrors
 * ProfileShareSheet/ProfileAddSheet.
 *
 * One shared file for native and web — it composes only the scaffold
 * primitives that have platform twins (ProfileSheetScaffold, ProfileGroupCard,
 * ProfileGroupRow, ProfileSectionHeader) plus plain RN primitives, exactly
 * like ProfileSettingsSheet does.
 *
 * Persistence: `Company.seoSettings` via PATCH /api/workspaces/company/:id.
 * The server patch-merges the object, and no GET currently returns it — so the
 * sheet seeds from the workspace payload when present, else defaults, and
 * autosaves optimistically with an inline Saving/Saved/Failed status (never
 * toast-only: toasts are invisible over iOS formSheets).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet, Switch, View } from 'react-native';
import { Text, TextInput } from '@/components/common/AppText';
import { useRouter } from 'expo-router';
import { ChevronLeft } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import type { CompanyPageLanguage, CompanySeoSettings } from '@tabbio/types';
import { useTheme } from '../../hooks/useTheme';
import { useHaptics } from '../../hooks/useHaptics';
import { useLocale } from '../../hooks/useLocale';
import { useWorkspace } from '../../providers/useWorkspace';
import { usePublicProfileBuilderModel } from './PublicProfileBuilderProvider';
import { getPublicProfileRoutes } from '../../lib/public-profile-routes';
import { buildPublicProfileUrl } from '../../lib/public-web';
import { DropdownField } from '../common/DropdownField';
import {
  ProfileGroupCard,
  ProfileGroupRow,
  ProfileSectionHeader,
  ProfileSheetScaffold,
} from './ProfileSheetScaffold';

const SEARCH_TITLE_MAX = 60;
const SEARCH_DESCRIPTION_MAX = 160;

const LANGUAGE_CODES: CompanyPageLanguage[] = ['en-AE', 'ar-AE', 'en', 'ar'];

type SeoDraft = {
  searchTitle: string;
  searchDescription: string;
  index: boolean;
  socialTitle: string;
  socialDescription: string;
  useSearchTextForSocial: boolean;
  language: CompanyPageLanguage;
};

function readInitialSeo(workspace: unknown): SeoDraft {
  const seo = (workspace as { seoSettings?: CompanySeoSettings } | null | undefined)
    ?.seoSettings;
  return {
    searchTitle: seo?.searchTitle ?? '',
    searchDescription: seo?.searchDescription ?? '',
    // Absent means indexable (server contract).
    index: seo?.index ?? true,
    socialTitle: seo?.socialTitle ?? '',
    socialDescription: seo?.socialDescription ?? '',
    useSearchTextForSocial: seo?.useSearchTextForSocial ?? true,
    language: seo?.language ?? 'en-AE',
  };
}

function toSeoSettings(draft: SeoDraft): CompanySeoSettings {
  return {
    searchTitle: draft.searchTitle.trim(),
    searchDescription: draft.searchDescription.trim(),
    index: draft.index,
    socialTitle: draft.socialTitle.trim(),
    socialDescription: draft.socialDescription.trim(),
    useSearchTextForSocial: draft.useSearchTextForSocial,
    language: draft.language,
  };
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

type CompanySettingsView = 'menu' | 'search';

export function CompanySettingsSections() {
  const { t } = useTranslation('common');
  const { theme } = useTheme();
  const router = useRouter();
  const haptics = useHaptics();
  const { isRTL } = useLocale();
  const { activeWorkspace, companyId, updateCompanyWorkspace } = useWorkspace();
  const { draftProfile, routeMode, hideBranding, setHideBranding } =
    usePublicProfileBuilderModel();

  const [view, setView] = useState<CompanySettingsView>('menu');
  const [seo, setSeo] = useState<SeoDraft>(() => readInitialSeo(activeWorkspace));
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const profileRoutes = useMemo(() => getPublicProfileRoutes(routeMode), [routeMode]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, []);

  const scheduleSave = (next: SeoDraft) => {
    if (!companyId) return;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setStatus('saving');
    setSaveErrorMessage(null);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      updateCompanyWorkspace(companyId, { seoSettings: toSeoSettings(next) })
        .then(() => {
          setStatus('saved');
        })
        .catch((error) => {
          setStatus('error');
          setSaveErrorMessage(
            error instanceof Error && error.message
              ? error.message
              : String(t('publicProfile.saveFailed')),
          );
        });
    }, 700);
  };

  const patchSeo = (partial: Partial<SeoDraft>) => {
    setSeo((previous) => {
      const next = { ...previous, ...partial };
      scheduleSave(next);
      return next;
    });
  };

  const tx = (key: string) => String(t(key as never));

  const languageLabels = useMemo(
    () =>
      Object.fromEntries(
        LANGUAGE_CODES.map((code) => [
          code,
          tx(`publicProfile.company.settings.languages.${code}`),
        ]),
      ) as Record<CompanyPageLanguage, string>,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );
  const languageOptions = LANGUAGE_CODES.map((code) => languageLabels[code]);

  const statusText =
    status === 'saving'
      ? tx('publicProfile.status.savingTitle')
      : status === 'saved'
        ? tx('publicProfile.company.settings.saved')
        : status === 'error'
          ? saveErrorMessage ?? tx('publicProfile.saveFailed')
          : null;

  // Menu subtitles read cheaply from state already on the client: the website
  // from the builder draft (identity form round-trip) and the canonical public
  // URL from the workspace slug (root form, PRD §14) — no extra fetches.
  const website = (draftProfile?.website ?? '').trim();
  const slug =
    typeof activeWorkspace.slug === 'string' ? activeWorkspace.slug.trim() : '';
  const pageUrl = slug ? buildPublicProfileUrl(slug, 'company') : null;
  const pageAddressLabel = pageUrl ? pageUrl.replace(/^https?:\/\//, '') : null;

  const openEditProfile = () => {
    haptics.trigger('light');
    router.push(profileRoutes.editProfile as never);
  };

  const soonLabel = (
    <Text style={[styles.soonLabel, { color: theme.textTertiary }]}>
      {tx('nav.soon')}
    </Text>
  );

  return (
    <ProfileSheetScaffold
      title={tx('publicProfile.company.settings.title')}
      subtitle={
        view === 'menu'
          ? tx('publicProfile.company.settings.subtitle')
          : tx('publicProfile.company.settings.menu.searchAppearanceHint')
      }
      compact
    >
      {view === 'menu' ? (
        <>
          {/* Page */}
          <ProfileSectionHeader
            title={tx('publicProfile.company.settings.menu.pageGroup')}
          />
          <ProfileGroupCard>
            <ProfileGroupRow
              label={tx('publicProfile.company.settings.menu.companyProfile')}
              description={tx(
                'publicProfile.company.settings.menu.companyProfileHint',
              )}
              onPress={openEditProfile}
              showChevron
            />
            <ProfileGroupRow
              label={tx('publicProfile.company.settings.menu.website')}
              description={
                website || tx('publicProfile.company.settings.menu.websiteHint')
              }
              onPress={openEditProfile}
              showChevron
              isLast
            />
          </ProfileGroupCard>

          {/* Publishing */}
          <ProfileSectionHeader
            title={tx('publicProfile.company.settings.menu.publishingGroup')}
          />
          <ProfileGroupCard>
            <ProfileGroupRow
              label={tx('publicProfile.company.settings.menu.pageAddress')}
              description={
                pageAddressLabel ??
                tx('publicProfile.company.settings.menu.pageAddressHint')
              }
              onPress={openEditProfile}
              showChevron
            />
            <ProfileGroupRow
              label={tx('publicProfile.company.settings.menu.searchAppearance')}
              description={tx(
                'publicProfile.company.settings.menu.searchAppearanceHint',
              )}
              onPress={() => {
                haptics.trigger('light');
                setView('search');
              }}
              showChevron
            />
            {/* P1 after launch (PRD §14): visible but disabled, sidebar
                Workspace-row "Soon" treatment. */}
            <ProfileGroupRow
              label={tx('publicProfile.company.settings.menu.domainEmbedding')}
              disabled
              rightElement={soonLabel}
            />
            <ProfileGroupRow
              label={tx('publicProfile.company.settings.menu.advancedSeo')}
              disabled
              rightElement={soonLabel}
              isLast
            />
          </ProfileGroupCard>
        </>
      ) : (
        <>
          <Pressable
            onPress={() => setView('menu')}
            style={({ pressed }) => [styles.backRow, { opacity: pressed ? 0.7 : 1 }]}
          >
            <ChevronLeft
              size={16}
              color={theme.textTertiary}
              strokeWidth={2}
              style={isRTL ? { transform: [{ rotate: '180deg' }] } : undefined}
            />
            <Text style={[styles.backLabel, { color: theme.textTertiary }]}>
              {tx('actions.back')}
            </Text>
          </Pressable>

          {statusText ? (
            <Text
              style={[
                styles.statusText,
                { color: status === 'error' ? theme.danger : theme.textTertiary },
              ]}
            >
              {statusText}
            </Text>
          ) : null}

          {/* Search appearance */}
          <ProfileSectionHeader
            title={tx('publicProfile.company.settings.searchSection')}
          />
          <View style={styles.fieldStack}>
            <LabeledInput
              label={tx('publicProfile.company.settings.searchTitle')}
              value={seo.searchTitle}
              onChangeText={(searchTitle) => patchSeo({ searchTitle })}
              maxLength={SEARCH_TITLE_MAX}
              counter={`${seo.searchTitle.length} / ${SEARCH_TITLE_MAX}`}
              placeholder={tx('publicProfile.company.settings.searchTitlePlaceholder')}
              theme={theme}
            />
            <LabeledInput
              label={tx('publicProfile.company.settings.searchDescription')}
              value={seo.searchDescription}
              onChangeText={(searchDescription) => patchSeo({ searchDescription })}
              maxLength={SEARCH_DESCRIPTION_MAX}
              counter={`${seo.searchDescription.length} / ${SEARCH_DESCRIPTION_MAX}`}
              placeholder={tx('publicProfile.company.settings.searchDescriptionPlaceholder')}
              multiline
              theme={theme}
            />
          </View>
          <ProfileGroupCard>
            <ProfileGroupRow
              label={tx('publicProfile.company.settings.indexing')}
              description={tx('publicProfile.company.settings.indexingHint')}
              rightElement={
                <Switch
                  value={seo.index}
                  onValueChange={(index) => patchSeo({ index })}
                  trackColor={{ false: theme.surfaceTertiary, true: '#22C55E' }}
                  thumbColor={theme.background}
                  ios_backgroundColor={theme.surfaceTertiary}
                />
              }
              isLast
            />
          </ProfileGroupCard>

          {/* Social sharing */}
          <ProfileSectionHeader
            title={tx('publicProfile.company.settings.socialSection')}
          />
          <ProfileGroupCard>
            <ProfileGroupRow
              label={tx('publicProfile.company.settings.useSearchText')}
              description={tx('publicProfile.company.settings.useSearchTextHint')}
              rightElement={
                <Switch
                  value={seo.useSearchTextForSocial}
                  onValueChange={(useSearchTextForSocial) =>
                    patchSeo({ useSearchTextForSocial })
                  }
                  trackColor={{ false: theme.surfaceTertiary, true: '#22C55E' }}
                  thumbColor={theme.background}
                  ios_backgroundColor={theme.surfaceTertiary}
                />
              }
              isLast
            />
          </ProfileGroupCard>
          {!seo.useSearchTextForSocial ? (
            <View style={styles.fieldStack}>
              <LabeledInput
                label={tx('publicProfile.company.settings.socialTitle')}
                value={seo.socialTitle}
                onChangeText={(socialTitle) => patchSeo({ socialTitle })}
                maxLength={SEARCH_TITLE_MAX}
                counter={`${seo.socialTitle.length} / ${SEARCH_TITLE_MAX}`}
                placeholder={tx('publicProfile.company.settings.socialTitlePlaceholder')}
                theme={theme}
              />
              <LabeledInput
                label={tx('publicProfile.company.settings.socialDescription')}
                value={seo.socialDescription}
                onChangeText={(socialDescription) => patchSeo({ socialDescription })}
                maxLength={SEARCH_DESCRIPTION_MAX}
                counter={`${seo.socialDescription.length} / ${SEARCH_DESCRIPTION_MAX}`}
                placeholder={tx('publicProfile.company.settings.socialDescriptionPlaceholder')}
                multiline
                theme={theme}
              />
            </View>
          ) : null}

          {/* Page language */}
          <ProfileSectionHeader
            title={tx('publicProfile.company.settings.languageSection')}
          />
          <DropdownField
            value={languageLabels[seo.language]}
            placeholder={languageLabels['en-AE']}
            options={languageOptions}
            onSelect={(selectedLabel) => {
              const code = LANGUAGE_CODES.find(
                (candidate) => languageLabels[candidate] === selectedLabel,
              );
              if (code) {
                patchSeo({ language: code });
              }
            }}
            title={tx('publicProfile.company.settings.languageSection')}
          />

          {/* Branding */}
          <ProfileSectionHeader title={tx('settings.preferences')} />
          <ProfileGroupCard>
            <ProfileGroupRow
              label={tx('publicProfile.settingsSheet.hideBranding')}
              description={tx('publicProfile.settingsSheet.hideBrandingHint')}
              rightElement={
                <Switch
                  value={hideBranding}
                  onValueChange={setHideBranding}
                  trackColor={{ false: theme.surfaceTertiary, true: '#22C55E' }}
                  thumbColor={theme.background}
                  ios_backgroundColor={theme.surfaceTertiary}
                />
              }
              isLast
            />
          </ProfileGroupCard>
        </>
      )}
    </ProfileSheetScaffold>
  );
}

function LabeledInput({
  label,
  value,
  onChangeText,
  placeholder,
  maxLength,
  counter,
  multiline = false,
  theme,
}: {
  label: string;
  value: string;
  onChangeText: (next: string) => void;
  placeholder: string;
  maxLength: number;
  counter: string;
  multiline?: boolean;
  theme: ReturnType<typeof useTheme>['theme'];
}) {
  return (
    <View style={styles.field}>
      <Text style={[styles.fieldLabel, { color: theme.textSecondary }]}>{label}</Text>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={theme.textTertiary}
        maxLength={maxLength}
        multiline={multiline}
        style={[
          multiline ? styles.textarea : styles.input,
          {
            borderColor: theme.border,
            color: theme.text,
            backgroundColor: theme.background,
          },
        ]}
        textAlignVertical={multiline ? 'top' : undefined}
      />
      <Text style={[styles.fieldCounter, { color: theme.textTertiary }]}>{counter}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  backRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 4,
  },
  backLabel: {
    fontSize: 13,
    fontWeight: '500',
  },
  soonLabel: {
    fontSize: 13,
    fontWeight: '500',
    paddingHorizontal: 4,
  },
  statusText: {
    fontSize: 12.5,
    lineHeight: 17,
    paddingHorizontal: 4,
  },
  fieldStack: {
    gap: 12,
  },
  field: {
    gap: 6,
  },
  fieldLabel: {
    fontSize: 13,
    fontWeight: '500',
    paddingHorizontal: 4,
  },
  fieldCounter: {
    fontSize: 12,
    paddingHorizontal: 4,
  },
  input: {
    borderRadius: 9999,
    borderWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 14,
  },
  textarea: {
    borderRadius: 18,
    borderWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 12,
    minHeight: 88,
    fontSize: 14,
    lineHeight: 20,
  },
});
